import { inject } from '@angular/core';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, tap } from 'rxjs';
import { exhaustMap, switchMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { IRoleState } from './role.state';
import { withRoleStateMethods } from './role.methods.state';
import { RoleService } from '../../services/roles/role.service';
import { NotificationService } from '../../services/notification/notification.service';
import { GlobalSpinnerService } from '../../services/global-spinner/global-spinner.service';
import { AppRole, CreateAppRole, EditAppRole, UpdateRolePermissions } from '../../models/roles/app-role.model';

export const withRoleApiMethods = signalStoreFeature(
    { state: type<IRoleState>() },
    withRoleStateMethods,
    withMethods((store) => {
        const roleService = inject(RoleService);
        const notificationService = inject(NotificationService);
        const spinner = inject(GlobalSpinnerService);

        return {
            loadRoles: rxMethod<void>(
                pipe(
                    tap(() => patchState(store, { loading: true, error: null })),
                    exhaustMap(() =>
                        roleService.getRoles().pipe(
                            tapResponse({
                                next: (res: { roles: AppRole[] }) => store.setRoles(res.roles),
                                error: (err: HttpErrorResponse) => {
                                    const msg = err.error?.message ?? err.message;
                                    store.setError(msg);
                                    notificationService.notifyError('Error', msg);
                                },
                            })
                        )
                    )
                )
            ),

            loadRoleByGuid: rxMethod<string>(
                pipe(
                    tap(() => patchState(store, { loading: true, error: null })),
                    switchMap((roleGuid) =>
                        roleService.getRoleByGuid(roleGuid).pipe(
                            tapResponse({
                                next: (res: { role: AppRole | null }) => store.setSelectedRole(res.role),
                                error: (err: HttpErrorResponse) => {
                                    const msg = err.error?.message ?? err.message;
                                    store.setError(msg);
                                    notificationService.notifyError('Error', msg);
                                },
                            })
                        )
                    )
                )
            ),

            loadModules: rxMethod<void>(
                pipe(
                    tap(() => patchState(store, { loading: true, error: null })),
                    exhaustMap(() =>
                        roleService.getModules().pipe(
                            tapResponse({
                                next: (res) => store.setModules(res.modules),
                                error: (err: HttpErrorResponse) => {
                                    const msg = err.error?.message ?? err.message;
                                    store.setError(msg);
                                    notificationService.notifyError('Error', msg);
                                },
                            })
                        )
                    )
                )
            ),

            createRole: rxMethod<{ payload: CreateAppRole; onSuccess?: () => void }>(
                pipe(
                    tap(() => patchState(store, { loading: true, error: null })),
                    exhaustMap(({ payload, onSuccess }) =>
                        roleService.createRole(payload).pipe(
                            tapResponse({
                                next: (res: { role: AppRole }) => {
                                    store.addRole(res.role);
                                    notificationService.notify('success', 'Created', 'Role created successfully.');
                                    onSuccess?.();
                                },
                                error: (err: HttpErrorResponse) => {
                                    const msg = err.error?.message ?? err.message;
                                    store.setError(msg);
                                    notificationService.notifyError('Error', msg);
                                },
                            })
                        )
                    )
                )
            ),

            editRole: rxMethod<{ guid: string; payload: EditAppRole; onSuccess?: () => void }>(
                pipe(
                    tap(() => patchState(store, { loading: true, error: null })),
                    exhaustMap(({ guid, payload, onSuccess }) =>
                        roleService.editRole(guid, payload).pipe(
                            tapResponse({
                                next: (res: { role: AppRole }) => {
                                    store.updateRole(res.role);
                                    notificationService.notify('success', 'Updated', 'Role updated successfully.');
                                    onSuccess?.();
                                },
                                error: (err: HttpErrorResponse) => {
                                    const msg = err.error?.message ?? err.message;
                                    store.setError(msg);
                                    notificationService.notifyError('Error', msg);
                                },
                            })
                        )
                    )
                )
            ),

            deleteRole: rxMethod<{ guid: string; onSuccess?: () => void }>(
                pipe(
                    tap(() => patchState(store, { loading: true, error: null })),
                    exhaustMap(({ guid, onSuccess }) =>
                        roleService.deleteRole(guid).pipe(
                            tapResponse({
                                next: () => {
                                    store.removeRole(guid);
                                    notificationService.notify('success', 'Deleted', 'Role deleted successfully.');
                                    onSuccess?.();
                                },
                                error: (err: HttpErrorResponse) => {
                                    const msg = err.error?.message ?? err.message;
                                    store.setError(msg);
                                    notificationService.notifyError('Error', msg);
                                },
                            })
                        )
                    )
                )
            ),

            updateRoleFeatures: rxMethod<{ guid: string; permissions: UpdateRolePermissions[]; onSuccess?: () => void }>(
                pipe(
                    tap(() => {
                        patchState(store, { loading: true, error: null });
                        spinner.show('Saving permissions...');
                    }),
                    exhaustMap(({ guid, permissions, onSuccess }) =>
                        roleService.updateRoleFeatures(guid, permissions).pipe(
                            tapResponse({
                                next: () => {
                                    patchState(store, { loading: false });
                                    spinner.hide();
                                    notificationService.notify('success', 'Saved', 'Permissions updated successfully.');
                                    onSuccess?.();
                                },
                                error: (err: HttpErrorResponse) => {
                                    const msg = err.error?.message ?? err.message;
                                    store.setError(msg);
                                    spinner.hide();
                                    notificationService.notifyError('Error', msg);
                                },
                            })
                        )
                    )
                )
            ),

            assignUserRole: rxMethod<{ userProfileGuid: string; roleGuid: string; onSuccess?: () => void }>(
                pipe(
                    tap(() => patchState(store, { loading: true, error: null })),
                    exhaustMap(({ userProfileGuid, roleGuid, onSuccess }) =>
                        roleService.assignUserRole(userProfileGuid, roleGuid).pipe(
                            tapResponse({
                                next: () => {
                                    patchState(store, { loading: false });
                                    notificationService.notify('success', 'Assigned', 'Role assigned successfully.');
                                    onSuccess?.();
                                },
                                error: (err: HttpErrorResponse) => {
                                    const msg = err.error?.message ?? err.message;
                                    store.setError(msg);
                                    notificationService.notifyError('Error', msg);
                                },
                            })
                        )
                    )
                )
            ),
        };
    })
);
